import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AUTH_KEYS from 'api/auth/auth.keys';
import { REGION } from 'constants/routes';
import i18n from 'i18n';
import { toastr } from 'utils';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from 'stores/authStore';
import { getPageParamFromLastPage } from 'utils/getPageParamFromLastPage';
import { applyMovePayload } from 'utils/reorder';
import REGIONS_KEYS from '../regions.keys';
import REGIONS_MUTATIONS from '../regions.mutation';
import REGIONS_QUERIES from '../regions.queries';

export const useFetchRegions = () =>
  useQuery({
    queryKey: REGIONS_KEYS.REGIONS(),
    queryFn: () => REGIONS_QUERIES.FETCH_REGIONS(),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useFetchRegionsSuperUserPaginated = ({
  page,
  pageSize,
  search,
  ordering,
  ...filters
} = {}) =>
  useQuery({
    queryKey: REGIONS_KEYS.REGIONS_SUPER_USER_PAGINATED({
      page,
      pageSize,
      search,
      ordering,
      ...filters,
    }),
    queryFn: () =>
      REGIONS_QUERIES.FETCH_REGIONS_SUPER_USER_PAGINATED({
        page,
        pageSize,
        search,
        ordering,
        ...filters,
      }),
    staleTime: 1 * 60 * 1000,
  });

export const useFetchRegionsSuperUserInfinite = ({ search } = {}) =>
  useInfiniteQuery({
    queryKey: REGIONS_KEYS.REGIONS_SUPER_USER_INFINITE({ search }),
    queryFn: ({ pageParam }) =>
      REGIONS_QUERIES.FETCH_REGIONS_SUPER_USER_PAGINATED({
        page: pageParam,
        pageSize: 30,
        search,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => getPageParamFromLastPage(lastPage),
    staleTime: 1 * 60 * 1000,
  });

export const useFetchRegionSuperUser = (uid) =>
  useQuery({
    queryKey: REGIONS_KEYS.REGION_SUPER_USER(uid),
    queryFn: () => REGIONS_QUERIES.FETCH_REGION_SUPER_USER(uid),
    enabled: !!uid,
    staleTime: 1 * 60 * 1000,
  });

export const useFetchRegion = (uid) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: REGIONS_KEYS.REGION(uid),
    queryFn: () => REGIONS_QUERIES.FETCH_REGION(uid),
    enabled: !!uid,
    staleTime: 1 * 60 * 1000, // 1 minute
    initialData: () => queryClient.getQueryData(REGIONS_KEYS.REGIONS())?.find((r) => r.uid === uid),
    initialDataUpdatedAt: () => queryClient.getQueryState(REGIONS_KEYS.REGIONS())?.dataUpdatedAt,
  });
};

export const useFetchSubscriptionPlans = (region) =>
  useQuery({
    queryKey: REGIONS_KEYS.SUBSCRIPTION_PLANS(region),
    queryFn: () => REGIONS_QUERIES.FETCH_SUBSCRIPTION_PLANS(region),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

export const useCreateRegion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: REGIONS_MUTATIONS.CREATE_REGION,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REGIONS_KEYS.REGIONS() });
    },
  });
};

export const useUpdateRegion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: REGIONS_MUTATIONS.UPDATE_REGION,
    onSuccess: (_, { uid }) => {
      queryClient.invalidateQueries({ queryKey: REGIONS_KEYS.REGION(uid) });
    },
  });
};

export const useUpdateCheckoutConfiguration = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: REGIONS_MUTATIONS.UPDATE_CHECKOUT_CONFIGURATION,
    onSuccess: (updatedConfiguration, { uid }) => {
      queryClient.setQueryData(REGIONS_KEYS.CHECKOUT_CONFIGURATION(uid), updatedConfiguration);
      queryClient.invalidateQueries({ queryKey: AUTH_KEYS.USER_PROFILE() });

      const { user } = useAuthStore.getState();
      if (user?.admin_details) {
        setUser({
          ...user,
          admin_details: {
            ...user.admin_details,
            checkout_configuration: updatedConfiguration,
          },
        });
      }

      navigate(REGION);
    },
  });
};

export const useDeleteRegion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: REGIONS_MUTATIONS.DELETE_REGION,
    onSuccess: () => {
      queryClient.invalidateQueries(REGIONS_KEYS.REGIONS());
      toastr.success(i18n.t('toastr:success'), i18n.t('toastr:regionDeletedSuccessfully'));
    },
  });
};

export const usePostShopifyRegionIntegration = () =>
  useMutation({
    mutationFn: REGIONS_MUTATIONS.POST_SHOPIFY_REGION_INTEGRATION,
  });

export const useSubscribeToRegionPlan = () =>
  useMutation({
    mutationFn: REGIONS_MUTATIONS.SUBSCRIBE_TO_REGION_PLAN,
  });

export const useCancelRegionSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: REGIONS_MUTATIONS.CANCEL_REGION_SUBSCRIPTION,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REGIONS_KEYS.REGIONS() });
    },
  });
};

export const useCheckRegionAvailability = (name, options = {}) =>
  useQuery({
    queryKey: REGIONS_KEYS.REGION_AVAILABILITY(name),
    queryFn: () => REGIONS_QUERIES.CHECK_REGION_AVAILABILITY(name),
    enabled: !!name && name.length > 0,
    retry: false,
    staleTime: 0,
    ...options,
  });

export const useCheckSubdomainAvailability = (subdomain, options = {}) =>
  useQuery({
    queryKey: REGIONS_KEYS.SUBDOMAIN_AVAILABILITY(subdomain),
    queryFn: () => REGIONS_QUERIES.CHECK_SUBDOMAIN_AVAILABILITY(subdomain),
    enabled: !!subdomain && subdomain.length > 0,
    retry: false,
    staleTime: 0,
    ...options,
  });

export const useGetHowToHubModules = (regionUid) =>
  useQuery({
    queryKey: REGIONS_KEYS.HOW_TO_HUB_MODULES(regionUid),
    queryFn: () => REGIONS_QUERIES.GET_HOW_TO_HUB_MODULES(regionUid),
    enabled: !!regionUid,
  });

export const useGetRegionPageModules = (regionUid) =>
  useQuery({
    queryKey: REGIONS_KEYS.REGION_PAGE_MODULES(regionUid),
    queryFn: () => REGIONS_QUERIES.GET_REGION_PAGE_MODULES(regionUid),
    enabled: !!regionUid,
  });

export const useGetRegionPageAvailableBanners = (regionUid) =>
  useQuery({
    queryKey: REGIONS_KEYS.REGION_PAGE_AVAILABLE_BANNERS(regionUid),
    queryFn: () => REGIONS_QUERIES.GET_REGION_PAGE_AVAILABLE_BANNERS(regionUid),
    enabled: !!regionUid,
  });

export const useGetRegionPageAvailableVideos = (regionUid) =>
  useQuery({
    queryKey: REGIONS_KEYS.REGION_PAGE_AVAILABLE_VIDEOS(regionUid),
    queryFn: () => REGIONS_QUERIES.GET_REGION_PAGE_AVAILABLE_VIDEOS(regionUid),
    enabled: !!regionUid,
  });

export const useGetRegionPageAvailableLooks = (regionUid) =>
  useQuery({
    queryKey: REGIONS_KEYS.REGION_PAGE_AVAILABLE_LOOKS(regionUid),
    queryFn: () => REGIONS_QUERIES.GET_REGION_PAGE_AVAILABLE_LOOKS(regionUid),
    enabled: !!regionUid,
  });

export const useGetRegionPageAvailableFavoriteProducts = (regionUid) =>
  useQuery({
    queryKey: REGIONS_KEYS.REGION_PAGE_AVAILABLE_FAVORITE_PRODUCTS(regionUid),
    queryFn: () => REGIONS_QUERIES.GET_REGION_PAGE_AVAILABLE_FAVORITE_PRODUCTS(regionUid),
    enabled: !!regionUid,
  });

export const useGetRegionPageAvailableParticipants = (regionUid) =>
  useQuery({
    queryKey: REGIONS_KEYS.REGION_PAGE_AVAILABLE_PARTICIPANTS(regionUid),
    queryFn: () => REGIONS_QUERIES.GET_REGION_PAGE_AVAILABLE_PARTICIPANTS(regionUid),
    enabled: !!regionUid,
  });

export const useGetRegionPageAvailableLocations = (regionUid) =>
  useQuery({
    queryKey: REGIONS_KEYS.REGION_PAGE_AVAILABLE_LOCATIONS(regionUid),
    queryFn: () => REGIONS_QUERIES.GET_REGION_PAGE_AVAILABLE_LOCATIONS(regionUid),
    enabled: !!regionUid,
  });

export const useGetRegionPageAvailableEvents = (regionUid) =>
  useQuery({
    queryKey: REGIONS_KEYS.REGION_PAGE_AVAILABLE_EVENTS(regionUid),
    queryFn: () => REGIONS_QUERIES.GET_REGION_PAGE_AVAILABLE_EVENTS(regionUid),
    enabled: !!regionUid,
  });

export const useGetRegionBanners = (regionUid) =>
  useQuery({
    queryKey: REGIONS_KEYS.REGION_BANNERS(regionUid),
    queryFn: () => REGIONS_QUERIES.GET_REGION_BANNERS(regionUid),
    enabled: !!regionUid,
  });

export const useGetRegionVideos = (regionUid) =>
  useQuery({
    queryKey: REGIONS_KEYS.REGION_VIDEOS(regionUid),
    queryFn: () => REGIONS_QUERIES.GET_REGION_VIDEOS(regionUid),
    enabled: !!regionUid,
  });

export const useAddHowToHubModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: REGIONS_MUTATIONS.ADD_HOW_TO_HUB_MODULE,
    onSuccess: (_, { regionUid }) => {
      queryClient.invalidateQueries({ queryKey: REGIONS_KEYS.HOW_TO_HUB_MODULES(regionUid) });
    },
  });
};

export const useAddRegionBanner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: REGIONS_MUTATIONS.ADD_REGION_BANNER,
    onSuccess: (_, { regionUid }) => {
      queryClient.invalidateQueries({ queryKey: REGIONS_KEYS.REGION_BANNERS(regionUid) });
    },
  });
};

export const useUpdateRegionBanner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: REGIONS_MUTATIONS.UPDATE_REGION_BANNER,
    onSuccess: (_, { regionUid }) => {
      queryClient.invalidateQueries({ queryKey: REGIONS_KEYS.REGION_BANNERS(regionUid) });
      queryClient.invalidateQueries({
        queryKey: REGIONS_KEYS.REGION_PAGE_AVAILABLE_BANNERS(regionUid),
      });
    },
  });
};

export const useAddRegionVideo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: REGIONS_MUTATIONS.ADD_REGION_VIDEO,
    onSuccess: (_, { regionUid }) => {
      queryClient.invalidateQueries({ queryKey: REGIONS_KEYS.REGION_VIDEOS(regionUid) });
    },
  });
};

export const useUpdateRegionVideo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: REGIONS_MUTATIONS.UPDATE_REGION_VIDEO,
    onSuccess: (_, { regionUid }) => {
      queryClient.invalidateQueries({ queryKey: REGIONS_KEYS.REGION_VIDEOS(regionUid) });
      queryClient.invalidateQueries({
        queryKey: REGIONS_KEYS.REGION_PAGE_AVAILABLE_VIDEOS(regionUid),
      });
    },
  });
};

export const useAddRegionPageModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: REGIONS_MUTATIONS.ADD_REGION_PAGE_MODULE,
    onSuccess: (_, { regionUid }) => {
      queryClient.invalidateQueries({ queryKey: REGIONS_KEYS.REGION_PAGE_MODULES(regionUid) });
    },
  });
};

export const useUpdateRegionPageModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: REGIONS_MUTATIONS.UPDATE_REGION_PAGE_MODULE,
    onSuccess: (_, { regionUid }) => {
      queryClient.invalidateQueries({ queryKey: REGIONS_KEYS.REGION_PAGE_MODULES(regionUid) });
    },
  });
};

export const useDeleteHowToHubModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: REGIONS_MUTATIONS.DELETE_HOW_TO_HUB_MODULE,
    onSuccess: (_, { regionUid }) => {
      queryClient.invalidateQueries({ queryKey: REGIONS_KEYS.HOW_TO_HUB_MODULES(regionUid) });
    },
  });
};

export const useDeleteRegionBanner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: REGIONS_MUTATIONS.DELETE_REGION_BANNER,
    onSuccess: (_, { regionUid }) => {
      queryClient.invalidateQueries({ queryKey: REGIONS_KEYS.REGION_BANNERS(regionUid) });
    },
  });
};

export const useDeleteRegionVideo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: REGIONS_MUTATIONS.DELETE_REGION_VIDEO,
    onSuccess: (_, { regionUid }) => {
      queryClient.invalidateQueries({ queryKey: REGIONS_KEYS.REGION_VIDEOS(regionUid) });
    },
  });
};

export const useDeleteRegionPageModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: REGIONS_MUTATIONS.DELETE_REGION_PAGE_MODULE,
    onSuccess: (_, { regionUid }) => {
      queryClient.invalidateQueries({ queryKey: REGIONS_KEYS.REGION_PAGE_MODULES(regionUid) });
    },
  });
};

export const useUpdateRegionPageModuleOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ regionUid, move }) =>
      REGIONS_MUTATIONS.MOVE_REGION_PAGE_MODULE({
        regionUid,
        ...move,
      }),
    onMutate: ({ regionUid, move }) => {
      const queryKey = REGIONS_KEYS.REGION_PAGE_MODULES(regionUid);
      queryClient.cancelQueries({ queryKey });

      const previousModules = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) => {
        if (!old) return old;
        return applyMovePayload(old, move);
      });

      return { previousModules };
    },
    onError: (_err, { regionUid }, context) => {
      if (context?.previousModules) {
        queryClient.setQueryData(
          REGIONS_KEYS.REGION_PAGE_MODULES(regionUid),
          context.previousModules,
        );
      }
    },
    onSettled: (_, __, { regionUid }) => {
      queryClient.invalidateQueries({ queryKey: REGIONS_KEYS.REGION_PAGE_MODULES(regionUid) });
    },
  });
};

export const useUpdateHowToHubModuleOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ regionUid, move }) =>
      REGIONS_MUTATIONS.MOVE_HOW_TO_HUB_MODULE({
        regionUid,
        ...move,
      }),
    onMutate: ({ regionUid, move }) => {
      const queryKey = REGIONS_KEYS.HOW_TO_HUB_MODULES(regionUid);
      queryClient.cancelQueries({ queryKey });

      const previousModules = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) => {
        if (!old) return old;
        return applyMovePayload(old, move);
      });

      return { previousModules };
    },
    onError: (_err, { regionUid }, context) => {
      if (context?.previousModules) {
        queryClient.setQueryData(
          REGIONS_KEYS.HOW_TO_HUB_MODULES(regionUid),
          context.previousModules,
        );
      }
    },
    onSettled: (_, __, { regionUid }) => {
      queryClient.invalidateQueries({ queryKey: REGIONS_KEYS.HOW_TO_HUB_MODULES(regionUid) });
    },
  });
};

export const useFetchRegionConfig = (uid) =>
  useQuery({
    queryKey: REGIONS_KEYS.REGION_CONFIG(uid),
    queryFn: () => REGIONS_QUERIES.FETCH_REGION_CONFIG(uid),
    enabled: !!uid,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

export const useRegionLiveFeed = (regionUid) =>
  useQuery({
    queryKey: REGIONS_KEYS.REGION_LIVE_FEED(regionUid),
    queryFn: () => REGIONS_QUERIES.FETCH_REGION_LIVE_FEED(regionUid),
    enabled: !!regionUid,
    staleTime: 1 * 60 * 1000,
  });
